/**
 * The Check form's "Broker" is free text, but a load stores broker_id → broker
 * row (DB FK, synced entity). Typed names were never written anywhere, so load
 * detail showed "—". Link by name: reuse an existing broker with the same name
 * (case/space-insensitive), else create one, then set load.brokerId. Called on
 * blur / Book / Pass, never per keystroke (no "T", "Te", "Tes" brokers).
 */
import { newId, type Broker, type Load } from "@haulsheet/core";
import { getDb } from "../db/dexie";
import { getSyncClient } from "../sync/SyncClient";

export function normalizeBrokerName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

function sameName(a: string, b: string): boolean {
  return normalizeBrokerName(a).toLowerCase() === normalizeBrokerName(b).toLowerCase();
}

export async function findBrokerByName(orgId: string, name: string): Promise<Broker | undefined> {
  const wanted = normalizeBrokerName(name);
  if (!wanted) return undefined;
  return getDb()
    .broker.where("orgId")
    .equals(orgId)
    .filter((b) => b.deletedAt == null && sameName(b.name, wanted))
    .first();
}

/** Point the load at the broker named `name` (creating it if new); blank clears it. Returns the broker id. */
export async function linkBrokerByName(loadId: string, name: string): Promise<string | null> {
  const db = getDb();
  const load = await db.load.get(loadId);
  if (!load) return null;
  const wanted = normalizeBrokerName(name);
  const client = getSyncClient();

  let brokerId: string | null = null;
  if (wanted) {
    const current = load.brokerId ? await db.broker.get(load.brokerId) : undefined;
    if (current && current.deletedAt == null && sameName(current.name, wanted)) return current.id;
    const found = await findBrokerByName(load.orgId, wanted);
    if (found) {
      brokerId = found.id;
    } else {
      const now = new Date().toISOString();
      const broker: Broker = {
        id: newId(),
        orgId: load.orgId,
        version: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        name: wanted,
      };
      await client.mutate({
        entity: "broker",
        op: "upsert",
        id: broker.id,
        baseVersion: null,
        patch: broker as unknown as Record<string, unknown>,
        row: broker as unknown as Record<string, unknown>,
      });
      brokerId = broker.id;
    }
  }
  if (brokerId === load.brokerId) return brokerId;
  // A different broker's contact no longer applies.
  const patch: Partial<Load> = { brokerId, brokerContactId: null };
  const fresh = (await db.load.get(loadId))!;
  await client.mutate({
    entity: "load",
    op: "upsert",
    id: loadId,
    baseVersion: fresh.version || null,
    patch: patch as Record<string, unknown>,
    row: { ...fresh, ...patch, updatedAt: new Date().toISOString() } as unknown as Record<string, unknown>,
  });
  return brokerId;
}
